import {
  AllCommunityModule,
  ModuleRegistry,
  colorSchemeDark,
  themeBalham,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAppContext } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  buildHeadersFromSchema,
  buildImportResultFromTableData,
  formatImportedRowData,
  generateObjectId,
  isCsvFile,
  isImageFile,
  parseCsv,
  processRow,
  type ImportSchemaField,
  type TextractResult,
} from "@/lib/importHelpers";
import { getTablePayload, isRecord } from "@/lib/tableBlockHelpers";
import { encrypt } from "@/utls/CryptoHelpers";

ModuleRegistry.registerModules([AllCommunityModule]);

const gridTheme = themeBalham.withPart(colorSchemeDark);

type TextractActionData = {
  success: boolean;
  message: string;
  textractResults: TextractResult[];
};

export function TableImportRoute() {
  const { id: tableId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { blocks, isLoading, addDecryptedBlock, updateDecryptedBlock } =
    useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<AgGridReact<Record<string, string>>>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [actionData, setActionData] = useState<TextractActionData | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<number, string>>(
    {},
  );
  const [saveAliases, setSaveAliases] = useState<Record<number, boolean>>({});

  const tableModel = useMemo(() => {
    if (!tableId) return null;
    const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
    if (!tableBlock) return null;
    const tableData = getTablePayload(tableBlock.data);
    if (!tableData) return null;
    return {
      block: tableBlock,
      name: tableData.name ?? "",
      schema: tableData.schema as ImportSchemaField[],
    };
  }, [blocks, tableId]);

  const schemaHeaders = useMemo(() => {
    if (!tableModel?.schema.length) return {};
    return buildHeadersFromSchema(tableModel.schema);
  }, [tableModel?.schema]);

  const breadcrumbTableLabel = isLoading
    ? "…"
    : tableModel
      ? tableModel.name || "Table"
      : "Table not found";

  useEffect(() => {
    if (!tableId) {
      setBreadcrumbSegments([]);
      return () => setBreadcrumbSegments([]);
    }
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: breadcrumbTableLabel, to: `/tables/${tableId}` },
      { label: "Import" },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [breadcrumbTableLabel, setBreadcrumbSegments, tableId]);

  useEffect(() => {
    if (actionData?.textractResults?.[0]?.columnMapping) {
      setColumnMapping(actionData.textractResults[0].columnMapping);
    }
  }, [actionData]);

  const submitFiles = useCallback(
    async (files: File[]) => {
      if (!tableId || files.length === 0) return;

      const csvFiles = files.filter(isCsvFile);
      const imageFiles = files.filter(isImageFile);
      if (csvFiles.length === 0 && imageFiles.length === 0) {
        console.error("TableImportRoute: no supported files (image or CSV)");
        return;
      }

      setIsSubmitting(true);
      try {
        const results: TextractResult[] = [];

        if (csvFiles.length > 0) {
          const csvResults = await Promise.all(
            csvFiles.map(async (file) => {
              const text = await file.text();
              return buildImportResultFromTableData({
                fileName: file.name,
                tableData: parseCsv(text),
                schemaHeaders,
                schema: tableModel?.schema ?? [],
                source: "csv",
              });
            }),
          );
          results.push(...csvResults);
        }

        if (imageFiles.length > 0) {
          const formData = new FormData();
          for (const file of imageFiles) {
            formData.append("images", file);
          }
          formData.append("tableId", tableId);
          if (Object.keys(schemaHeaders).length > 0) {
            formData.append("schemaHeaders", JSON.stringify(schemaHeaders));
          }
          if (tableModel?.schema.length) {
            formData.append("schema", JSON.stringify(tableModel.schema));
          }

          const res = await fetch("/api/import/textract", {
            method: "POST",
            body: formData,
          });
          if (res.ok) {
            const data = (await res.json()) as TextractActionData;
            results.push(...(data.textractResults ?? []));
          } else {
            console.error(
              "TableImportRoute: textract failed",
              await res.text(),
            );
            if (results.length === 0) return;
          }
        }

        if (results.length === 0) return;

        const parts: string[] = [];
        const csvCount = results.filter((r) => r.source === "csv").length;
        const textractCount = results.length - csvCount;
        if (csvCount > 0) {
          parts.push(`${csvCount} CSV file${csvCount === 1 ? "" : "s"}`);
        }
        if (textractCount > 0) {
          parts.push(
            `${textractCount} image${textractCount === 1 ? "" : "s"} with AWS Textract`,
          );
        }

        setActionData({
          success: true,
          message: `Processed ${parts.join(" and ")}`,
          textractResults: results,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [schemaHeaders, tableId, tableModel?.schema],
  );

  const handleSubmitFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    void submitFiles(Array.from(event.target.files));
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!event.clipboardData.items) return;
    const imageFiles = Array.from(event.clipboardData.items)
      .filter((item) => item.type.includes("image"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    await submitFiles(imageFiles);
  };

  const handleImport = useCallback(
    async ({ continueImport }: { continueImport?: boolean }) => {
      if (!tableId || !actionData?.textractResults?.length || !tableModel) return;

      const secretKey =
        typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
      if (!secretKey) {
        console.warn("TableImportRoute: no accessKey in localStorage");
        return;
      }

      setIsImporting(true);
      try {
        const result = actionData.textractResults[0];
        const originalHeaders = result.originalHeaders ?? [];
        let schema = [...tableModel.schema];

        if (Object.keys(saveAliases).length > 0) {
          schema = schema.map((column) => {
            const columnCopy = { ...column };
            for (const [colIndex, shouldSave] of Object.entries(saveAliases)) {
              if (
                !shouldSave ||
                columnMapping[Number(colIndex)] !== column.key
              ) {
                continue;
              }
              const alias = originalHeaders[Number(colIndex)];
              if (!alias) continue;
              const aliases = [...(columnCopy.importAliases ?? [])];
              const normalizedAlias = alias.toLowerCase().trim();
              const exists = aliases.some(
                (existing) => existing.toLowerCase().trim() === normalizedAlias,
              );
              if (!exists) aliases.push(alias);
              columnCopy.importAliases = aliases;
            }
            return columnCopy;
          });

          const existingTableData = tableModel.block.data;
          if (isRecord(existingTableData)) {
            const tableData = { ...existingTableData, schema };
            const encryptedData = await encrypt(
              JSON.stringify(tableData),
              secretKey,
            );
            const res = await fetch(
              `/api/blocks/${encodeURIComponent(tableId)}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ encryptedData }),
              },
            );
            if (res.ok) {
              updateDecryptedBlock({
                block: { id: tableId, type: "table", data: tableData },
              });
            } else {
              console.error(
                "TableImportRoute: failed to save aliases",
                await res.text(),
              );
            }
          }
        }

        const selectedRows = tableRef.current?.api.getSelectedRows() ?? [];
        const processedBlocks =
          selectedRows.length > 0
            ? selectedRows.map((row) =>
                processRow({
                  row: Object.values(row),
                  columnMapping,
                  schema,
                }),
              )
            : actionData.textractResults.flatMap((textractResult) =>
                textractResult.textractResponse.map((row) =>
                  processRow({ row, columnMapping, schema }),
                ),
              );

        const blockPayloads = await Promise.all(
          processedBlocks.map(async (block) => {
            const rowData = formatImportedRowData({ block, schema });
            const blockData = { parent: tableId, data: rowData };
            const blockId = generateObjectId();
            const encryptedData = await encrypt(
              JSON.stringify(blockData),
              secretKey,
            );
            return { _id: blockId, encryptedData, blockData };
          }),
        );

        const res = await fetch("/api/blocks/bulk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            blocks: blockPayloads.map(({ _id, encryptedData }) => ({
              _id,
              encryptedData,
            })),
          }),
        });

        if (!res.ok) {
          console.error("TableImportRoute: bulk create failed", await res.text());
          return;
        }

        for (const { _id, blockData } of blockPayloads) {
          addDecryptedBlock({
            block: { id: _id, data: blockData },
          });
        }

        if (continueImport) {
          setActionData(null);
          setColumnMapping({});
          setSaveAliases({});
          return;
        }

        navigate(`/tables/${tableId}`);
      } finally {
        setIsImporting(false);
      }
    },
    [
      actionData,
      addDecryptedBlock,
      columnMapping,
      navigate,
      saveAliases,
      tableId,
      tableModel,
      updateDecryptedBlock,
    ],
  );

  if (!isLoading && !tableModel) {
    return (
      <p className="text-muted-foreground p-8 text-sm">
        This table does not exist or could not be loaded.
      </p>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-1 flex-col p-6">
      <div className="mb-3 text-xl font-bold">Import rows</div>

      {isSubmitting ? (
        <div className="text-muted-foreground flex items-center justify-center gap-4 py-12 text-lg">
          Processing
          <Loader2 className="size-5 animate-spin" aria-hidden />
        </div>
      ) : actionData ? (
        <div className="mt-4">
          <ul id="table-response">
            {actionData.textractResults.map((result, index) => (
              <li key={index} className="mb-5">
                <div className="border-border bg-card mb-6 rounded border p-4">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="font-semibold">Custom column mapping</div>
                      <button
                        type="button"
                        className="text-primary hover:text-primary/80 cursor-pointer underline"
                        onClick={() => {
                          setColumnMapping({});
                          setSaveAliases({});
                        }}
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3 lg:grid-cols-5">
                      {(result.textractResponse[0] ?? []).map((_, colIndex) => {
                        const originalHeader = result.originalHeaders?.[colIndex] ?? "";
                        const mappedKey = columnMapping[colIndex];
                        const isExactMatch =
                          mappedKey && originalHeader
                            ? originalHeader.toLowerCase().trim() ===
                                schemaHeaders[mappedKey]?.toLowerCase().trim() ||
                              originalHeader.toLowerCase().trim() ===
                                mappedKey.toLowerCase().trim()
                            : false;
                        const showSaveAlias =
                          !!mappedKey && !!originalHeader && !isExactMatch;

                        return (
                          <div key={colIndex} className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <label className="text-muted-foreground min-w-6 text-sm">
                                {colIndex + 1}:
                              </label>
                              <select
                                value={columnMapping[colIndex] ?? ""}
                                onChange={(e) =>
                                  setColumnMapping({
                                    ...columnMapping,
                                    [colIndex]: e.target.value,
                                  })
                                }
                                className="border-input bg-background focus-visible:ring-ring flex-1 rounded border px-2 py-1 text-sm outline-none focus-visible:ring-2"
                              >
                                <option value="">— Unmapped —</option>
                                {Object.entries(schemaHeaders).map(([key, value]) => {
                                  const isMappedElsewhere =
                                    Object.values(columnMapping).includes(key);
                                  const isCurrent = columnMapping[colIndex] === key;
                                  if (isMappedElsewhere && !isCurrent) return null;
                                  return (
                                    <option key={key} value={key}>
                                      {value}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {showSaveAlias ? (
                              <label className="text-muted-foreground ml-7 flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={saveAliases[colIndex] ?? false}
                                  onChange={(e) =>
                                    setSaveAliases({
                                      ...saveAliases,
                                      [colIndex]: e.target.checked,
                                    })
                                  }
                                />
                                <span>Save &quot;{originalHeader}&quot; as alias</span>
                              </label>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                <div className="mk-table-ag-grid w-full min-w-0">
                  <AgGridReact
                    ref={tableRef}
                    theme={gridTheme}
                    columnDefs={(result.textractResponse[0] ?? []).map((_, colIdx) => ({
                      headerName: columnMapping[colIdx]
                        ? `${colIdx + 1}: ${schemaHeaders[columnMapping[colIdx]] || columnMapping[colIdx]}`
                        : `${colIdx + 1}: Unmapped`,
                      field: `col${colIdx}`,
                    }))}
                    rowData={result.textractResponse.map((row) => {
                      const rowData: Record<string, string> = {};
                      row.forEach((cell, colIdx) => {
                        rowData[`col${colIdx}`] = cell;
                      });
                      return rowData;
                    })}
                    domLayout="autoHeight"
                    rowSelection={{ mode: "multiRow" }}
                  />
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-5 rounded py-4">
            <div className="flex flex-wrap gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Reset
              </Button>
              <Button
                type="button"
                disabled={isImporting}
                onClick={() => void handleImport({ continueImport: true })}
              >
                {isImporting ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  "Insert and continue"
                )}
              </Button>
              <Button
                type="button"
                disabled={isImporting}
                onClick={() => void handleImport({})}
              >
                {isImporting ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  "Insert and go to table"
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <label htmlFor="images" className="mb-2 block font-medium">
              Select image or CSV files, or paste images
            </label>
            <div
              onPaste={(e) => void handlePaste(e)}
              className="border-border hover:border-primary/50 hover:bg-muted/30 relative mb-4 min-h-20 cursor-pointer rounded-md border border-dashed p-1 focus:border-primary"
              tabIndex={0}
            >
              <div className="text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm">
                Drag and drop or{" "}
                <span className="text-primary relative py-4 hover:underline">
                  browse files
                  <input
                    type="file"
                    id="images"
                    name="images"
                    multiple
                    accept="image/*,.csv,text/csv"
                    onChange={handleSubmitFile}
                    ref={fileInputRef}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  />
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(tableId ? `/tables/${tableId}` : "/tables")}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
